import React, { useMemo } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import Modal, { ModalProps } from "components/Modal";
import { useTranslation } from "react-i18next";
import Table from "components/Table";
import { ColumnDef } from "@tanstack/react-table";
import Button from "components/Button";
import clsx from "clsx";
import useMappingEditor from "features/MappingEditor/useMappingEditor";

interface Props extends ModalProps {
  components: string[];
  affectedCount: number;
}

const ComponentsUpdateResponsePopup = (props: Props) => {
  const { t } = useTranslation();
  const { showUpdatedToastMessage } = useMappingEditor();

  const getColumns = () =>
    useMemo<ColumnDef<{ [key: string]: string }>[]>(
      () => [
        {
          accessorFn: (row) => row.component,
          id: "components",
          cell: (info) => (
            <div className="whitespace-nowrap">
              {info.row.original.component}
            </div>
          ),
          header: () => <div>{t("mappingEditor.nonUpdatedComponents")}</div>,
          enableColumnFilter: false,
          enableSorting: true,
        },
      ],
      []
    );

  const componentsList = props.components.map((component) => ({
    component: component,
  }));

  const onClickContinue = () => {
    props.close();
    showUpdatedToastMessage(props.affectedCount);
  };

  return (
    <Modal
      isOpen={props.open}
      setIsOpen={props.close}
      title={t("mappingEditor.componentsNeedManualProcessing")}
      containerClassName="min-w-[650px]"
      footer={
        <Button type="primary" onClick={onClickContinue} width="fit-content">
          {t("mappingEditor.continue")}
        </Button>
      }
      footerClassName="flex !justify-end"
      disableOutsideClick
    >
      <div className="p-6">
        <div className="flex text-yellow-700 items-center">
          <div className="bg-yellow-100 rounded-full flex items-center justify-center mr-2 min-w-[24px] min-h-[24px]">
            <IconAlertTriangle stroke={3} size={14} />
          </div>
          <div className="text-xs font-medium">
            {t("mappingEditor.componentsNotSupportedAlert")}
          </div>
        </div>
        <div className="text-sm leading-5 font-normal my-4 text-gray-700">
          {t("mappingEditor.componentsNotSupportedDesc")}
        </div>
        <Table
          data={componentsList}
          getColumns={getColumns as () => ColumnDef<unknown, unknown>[]}
          headerRowStyle="bg-white whitespace-nowrap"
          headerColumnStyle="border-r last-of-type:border-r-0 bg-gray-50"
          headerDivStyle="!py-1"
          customColumnStyle="!py-2 !px-2 bg-white !text-sm font-medium text-gray-900"
          scrollDivClassName="!max-h-[200px] rounded-b-none"
          enableTooltip
          borderedStyle
          noFooterBar
        />
        <div
          className={clsx(
            "bg-gray-50 border-t-0 border-x border-b border-gray-300 text-xs",
            "font-medium text-gray-500 px-4 py-2 rounded-bl-lg rounded-br-lg"
          )}
        >
          {`${props.components.length} ${t(
            props.components.length === 1
              ? "mappingEditor.component"
              : "mappingEditor.components"
          )}`}
        </div>
      </div>
    </Modal>
  );
};

export default ComponentsUpdateResponsePopup;
